import { useCallback, useEffect, useState } from "react";
import { Plus, X, Trash, PaperPlaneTilt, ChartBar, UserPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { saCard, saInput, saLabel, fmtD, SABadge } from "../../components/saShared";

const LEAD_STATUS = {
  nuovo: { label: "Nuovo", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  ricontattato: { label: "Ricontattato", cls: "bg-amber-400/15 text-amber-300 border-amber-400/40" },
  convertito: { label: "Convertito", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  perso: { label: "Perso", cls: "bg-zinc-700/40 text-zinc-400 border-zinc-600" },
};
const LEAD_KEYS = Object.keys(LEAD_STATUS);
const EMPTY_LEAD = { nome: "", ristorante: "", email: "", telefono: "", messaggio: "" };
const EMPTY_CAMP = { name: "", subject: "", body: "", lead_statuses: ["nuovo", "ricontattato"] };

export default function SAComunicazioni() {
  const [tab, setTab] = useState("lead");
  const [leads, setLeads] = useState([]);
  const [leadFilter, setLeadFilter] = useState("");
  const [showLead, setShowLead] = useState(false);
  const [leadForm, setLeadForm] = useState(EMPTY_LEAD);
  const [camps, setCamps] = useState([]);
  const [showCamp, setShowCamp] = useState(false);
  const [campForm, setCampForm] = useState(EMPTY_CAMP);
  const [statsFor, setStatsFor] = useState(null);
  const [stats, setStats] = useState(null);
  const [recipients, setRecipients] = useState([]);

  const loadLeads = useCallback(() => {
    api.get(`/superadmin/leads${leadFilter ? `?status=${leadFilter}` : ""}`).then((r) => setLeads(r.data)).catch(() => {});
  }, [leadFilter]);
  const loadCamps = useCallback(() => {
    api.get("/superadmin/comunicazioni").then((r) => setCamps(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { loadCamps(); }, [loadCamps]);

  const addLead = async (e) => {
    e.preventDefault();
    try {
      await api.post("/superadmin/leads", leadForm);
      toast.success("Lead aggiunto");
      setShowLead(false); setLeadForm(EMPTY_LEAD); loadLeads();
    } catch (err) { toast.error(apiError(err)); }
  };
  const setLeadStatus = async (id, status) => {
    try { await api.patch(`/superadmin/leads/${id}`, { status }); loadLeads(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const delLead = async (id) => {
    if (!window.confirm("Eliminare questo lead?")) return;
    await api.delete(`/superadmin/leads/${id}`); loadLeads();
  };

  const addCamp = async (e) => {
    e.preventDefault();
    try {
      await api.post("/superadmin/comunicazioni", campForm);
      toast.success("Campagna creata (bozza)");
      setShowCamp(false); setCampForm(EMPTY_CAMP); loadCamps();
    } catch (err) { toast.error(apiError(err)); }
  };
  const sendCamp = async (id) => {
    if (!window.confirm("Inviare ora la campagna a tutti i lead selezionati?")) return;
    try {
      const r = await api.post(`/superadmin/comunicazioni/${id}/send`);
      toast.success(`Campagna inviata a ${r.data.destinatari} lead`);
      loadCamps();
    } catch (e) { toast.error(apiError(e)); }
  };
  const delCamp = async (id) => {
    if (!window.confirm("Eliminare la campagna e i suoi eventi?")) return;
    await api.delete(`/superadmin/comunicazioni/${id}`); loadCamps();
  };
  const openStats = async (c) => {
    setStatsFor(c); setStats(null); setRecipients([]);
    try {
      const [s, rc] = await Promise.all([
        api.get(`/superadmin/comunicazioni/${c.id}/stats`),
        api.get(`/superadmin/comunicazioni/${c.id}/recipients`)]);
      setStats(s.data); setRecipients(rc.data);
    } catch { toast.error("Errore caricamento statistiche"); }
  };

  const fmtT = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : null;

  return (
    <div className="space-y-4" data-testid="sa-comunicazioni">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-white">Comunicazioni</h1>
        {tab === "lead" ? (
          <button onClick={() => setShowLead(true)} data-testid="sa-lead-new"
            className="h-11 px-4 rounded-md bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2 hover:bg-amber-300 transition">
            <UserPlus size={16} weight="bold" /> Nuovo lead
          </button>
        ) : (
          <button onClick={() => setShowCamp(true)} data-testid="sa-camp-new"
            className="h-11 px-4 rounded-md bg-amber-400 text-zinc-950 text-sm font-bold flex items-center gap-2 hover:bg-amber-300 transition">
            <Plus size={16} weight="bold" /> Nuova campagna
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-zinc-800">
        {[["lead", `Lead (${leads.length})`], ["campagne", "Campagne marketing"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sa-comm-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === k ? "border-amber-400 text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === "lead" && (
        <div className="space-y-3">
          <div className="flex gap-1.5 flex-wrap">
            {[{ key: "", label: "Tutti" }, ...LEAD_KEYS.map((k) => ({ key: k, label: LEAD_STATUS[k].label }))].map((f) => (
              <button key={f.key} onClick={() => setLeadFilter(f.key)} data-testid={`lead-filter-${f.key || "tutti"}`}
                className={`h-8 px-3 rounded-full text-[11px] font-bold border transition ${leadFilter === f.key ? "bg-amber-400 text-zinc-950 border-amber-400" : "bg-zinc-900 text-zinc-400 border-zinc-700"}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className={`${saCard} overflow-x-auto`}>
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                  {["Data", "Nome", "Ristorante", "Contatti", "Messaggio", "Fonte", "Stato", ""].map((h) => (
                    <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {leads.map((l) => (
                  <tr key={l.id} className="hover:bg-zinc-800/40" data-testid={`lead-row-${l.id}`}>
                    <td className="px-4 py-3 text-xs text-zinc-400">{fmtD(l.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-zinc-100">{l.nome}</td>
                    <td className="px-4 py-3 text-zinc-300">{l.ristorante || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-400">
                      <div>{l.email}</div>{l.telefono && <div>{l.telefono}</div>}
                    </td>
                    <td className="px-4 py-3 text-xs text-zinc-400 max-w-[220px] truncate" title={l.messaggio}>{l.messaggio || "—"}</td>
                    <td className="px-4 py-3 text-xs text-zinc-500 capitalize">
                      <div>{l.source}</div>
                      {l.seq === "checklist" && (
                        <span data-testid={`lead-seq-${l.id}`}
                          title={(l.seq_sent || []).map((s) => `step ${s.step}: ${s.status}`).join(" · ") || "In coda"}
                          className="inline-block mt-1 px-2 py-0.5 rounded-full bg-[#2ce0cb]/10 border border-[#2ce0cb]/30 text-[#2ce0cb] text-[10px] font-bold normal-case">
                          {l.seq_step >= 3 ? "Sequenza completata" : `Sequenza ${l.seq_step || 0}/3`}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)}
                        data-testid={`lead-status-${l.id}`}
                        className={`text-[11px] font-bold px-2 py-1 rounded-full border bg-zinc-900 ${LEAD_STATUS[l.status]?.cls || ""}`}>
                        {LEAD_KEYS.map((k) => <option key={k} value={k}>{LEAD_STATUS[k].label}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => delLead(l.id)} data-testid={`lead-del-${l.id}`}
                        className="p-2 text-zinc-500 hover:text-rose-400 transition"><Trash size={15} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {leads.length === 0 && <div className="py-10 text-center text-sm text-zinc-500">Nessun lead. Il form pubblico è su <b>/richiedi-info</b>.</div>}
          </div>
        </div>
      )}

      {tab === "campagne" && (
        <div className="grid gap-3 lg:grid-cols-2" data-testid="sa-camps">
          {camps.length === 0 && (
            <div className={`${saCard} col-span-full py-10 text-center text-sm text-zinc-500`}>
              Nessuna campagna. Creane una per ricontattare i lead via email (Brevo, statistiche reali).
            </div>
          )}
          {camps.map((c) => (
            <div key={c.id} className={`${saCard} p-4 space-y-3`} data-testid={`sa-camp-${c.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-white truncate">{c.name}</div>
                  <div className="text-xs text-zinc-500">{fmtD(c.created_at)} · destinatari: {(c.lead_statuses || []).join(", ")}</div>
                </div>
                <SABadge map={{ bozza: { label: "Bozza", cls: "bg-zinc-700/40 text-zinc-300 border-zinc-600" }, completata: { label: "Inviata", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" } }} value={c.status} />
              </div>
              <div className="text-xs text-zinc-400">Oggetto: <b className="text-zinc-200">{c.content?.subject}</b></div>
              {c.status === "completata" && c.stats && (
                <div className="flex flex-wrap gap-1.5 text-[10px] font-bold">
                  {[["Inviate", c.stats.sent], ["Consegnate", c.stats.delivered], ["Aperture", c.stats.opened],
                    ["Click", c.stats.clicked], ["Bounce", c.stats.bounced], ["Disiscr.", c.stats.unsubscribed]].map(([l, v]) => (
                    <span key={l} className="px-2 py-1 rounded-full bg-zinc-800 text-zinc-300 border border-zinc-700">{l}: {v || 0}</span>
                  ))}
                </div>
              )}
              <div className="flex gap-2 pt-1">
                {c.status === "bozza" && (
                  <button onClick={() => sendCamp(c.id)} data-testid={`sa-camp-send-${c.id}`}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-amber-400 text-zinc-950 text-xs font-bold hover:bg-amber-300 transition">
                    <PaperPlaneTilt size={13} weight="bold" /> Invia ora
                  </button>
                )}
                <button onClick={() => openStats(c)} data-testid={`sa-camp-stats-${c.id}`}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-md bg-zinc-800 text-zinc-300 text-xs font-bold border border-zinc-700 hover:border-amber-400/40 transition">
                  <ChartBar size={13} weight="bold" /> Statistiche
                </button>
                <button onClick={() => delCamp(c.id)} data-testid={`sa-camp-del-${c.id}`}
                  className="h-8 px-3 rounded-md text-xs font-bold text-rose-400 border border-rose-500/30 hover:bg-rose-500/10 transition">Elimina</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showLead && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setShowLead(false)}>
          <form onSubmit={addLead} className={`${saCard} w-full sm:max-w-md rounded-t-xl sm:rounded-xl p-5 space-y-3`}
            onClick={(e) => e.stopPropagation()} data-testid="sa-lead-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-white">Nuovo lead</h3>
              <button type="button" onClick={() => setShowLead(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div><label className={saLabel}>Nome *</label>
              <input required value={leadForm.nome} onChange={(e) => setLeadForm({ ...leadForm, nome: e.target.value })} className={saInput} data-testid="sa-lead-nome" /></div>
            <div><label className={saLabel}>Ristorante</label>
              <input value={leadForm.ristorante} onChange={(e) => setLeadForm({ ...leadForm, ristorante: e.target.value })} className={saInput} data-testid="sa-lead-ristorante" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><label className={saLabel}>Email *</label>
                <input required type="email" value={leadForm.email} onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })} className={saInput} data-testid="sa-lead-email" /></div>
              <div><label className={saLabel}>Telefono</label>
                <input value={leadForm.telefono} onChange={(e) => setLeadForm({ ...leadForm, telefono: e.target.value })} className={saInput} data-testid="sa-lead-telefono" /></div>
            </div>
            <div><label className={saLabel}>Note</label>
              <textarea value={leadForm.messaggio} onChange={(e) => setLeadForm({ ...leadForm, messaggio: e.target.value })} rows={2} className={`${saInput} h-auto py-2`} data-testid="sa-lead-messaggio" /></div>
            <button className="w-full h-12 rounded-md bg-amber-400 text-zinc-950 font-bold text-sm" data-testid="sa-lead-submit">Aggiungi lead</button>
          </form>
        </div>
      )}

      {showCamp && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setShowCamp(false)}>
          <form onSubmit={addCamp} className={`${saCard} w-full sm:max-w-lg rounded-t-xl sm:rounded-xl p-5 space-y-3 max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()} data-testid="sa-camp-form">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-lg text-white">Nuova campagna marketing</h3>
              <button type="button" onClick={() => setShowCamp(false)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            <div><label className={saLabel}>Nome interno *</label>
              <input required value={campForm.name} onChange={(e) => setCampForm({ ...campForm, name: e.target.value })} className={saInput} placeholder='Es. "Follow-up lead maggio"' data-testid="sa-camp-name" /></div>
            <div><label className={saLabel}>Oggetto email *</label>
              <input required value={campForm.subject} onChange={(e) => setCampForm({ ...campForm, subject: e.target.value })} className={saInput} data-testid="sa-camp-subject" /></div>
            <div><label className={saLabel}>Testo * (usa {"{{nome_cliente}}"} per il nome del lead)</label>
              <textarea required value={campForm.body} onChange={(e) => setCampForm({ ...campForm, body: e.target.value })} rows={5}
                className={`${saInput} h-auto py-2`} data-testid="sa-camp-body" /></div>
            <div>
              <label className={saLabel}>Destinatari (stato lead)</label>
              <div className="flex gap-2 flex-wrap mt-1">
                {LEAD_KEYS.map((k) => (
                  <label key={k} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-bold cursor-pointer transition ${
                    campForm.lead_statuses.includes(k) ? "bg-amber-400/15 text-amber-300 border-amber-400/40" : "bg-zinc-900 text-zinc-500 border-zinc-700"}`}>
                    <input type="checkbox" className="hidden" checked={campForm.lead_statuses.includes(k)}
                      onChange={(e) => setCampForm({
                        ...campForm,
                        lead_statuses: e.target.checked
                          ? [...campForm.lead_statuses, k]
                          : campForm.lead_statuses.filter((x) => x !== k),
                      })} data-testid={`sa-camp-filter-${k}`} />
                    {LEAD_STATUS[k].label}
                  </label>
                ))}
              </div>
            </div>
            <button className="w-full h-12 rounded-md bg-amber-400 text-zinc-950 font-bold text-sm" data-testid="sa-camp-submit">Crea campagna</button>
          </form>
        </div>
      )}

      {statsFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" onClick={() => setStatsFor(null)}>
          <div className={`${saCard} w-full sm:max-w-xl rounded-t-xl sm:rounded-xl p-5 max-h-[90vh] overflow-y-auto`}
            onClick={(e) => e.stopPropagation()} data-testid="sa-camp-stats-dialog">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-bold text-lg text-white truncate">{statsFor.name}</h3>
              <button onClick={() => setStatsFor(null)} className="p-2 text-zinc-500"><X size={18} /></button>
            </div>
            {stats ? (
              <>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
                  {[["Inviate", stats.sent], ["Consegnate", stats.delivered], ["Aperture", `${stats.opened} (${stats.tasso_apertura}%)`],
                    ["Click", `${stats.clicked} (${stats.ctr}%)`], ["Bounce", stats.bounced], ["Spam", stats.failed],
                    ["Disiscritti", stats.unsubscribed], ["Destinatari", stats.recipients_count]].map(([l, v]) => (
                    <div key={l} className="bg-zinc-900 border border-zinc-800 rounded-lg p-2.5" data-testid={`sa-metric-${l.toLowerCase()}`}>
                      <div className="text-[9px] uppercase tracking-wider font-bold text-zinc-500">{l}</div>
                      <div className="font-display text-lg font-black text-white">{v ?? 0}</div>
                    </div>
                  ))}
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5">Destinatari ({recipients.length})</div>
                <div className="border border-zinc-800 rounded-lg divide-y divide-zinc-800/60 max-h-64 overflow-y-auto">
                  {recipients.map((r) => (
                    <div key={r.lead_id} className="px-3 py-2.5" data-testid={`sa-recipient-${r.lead_id}`}>
                      <div className="flex items-baseline justify-between gap-2">
                        <span className="text-sm font-semibold text-zinc-100">{r.nome} {r.ristorante && <span className="text-zinc-500 font-normal">· {r.ristorante}</span>}</span>
                        <span className="text-[11px] text-zinc-500">{r.email}</span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {[["Inviato", r.inviato_il, "text-zinc-400"], ["Consegnato", r.consegnato_il, "text-emerald-400"],
                          ["Aperto", r.aperto_il, "text-sky-400"], ["Click", r.cliccato_il, "text-cyan-300"],
                          ["Bounce", r.bounce_il, "text-amber-400"], ["Spam", r.spam_il, "text-rose-400"],
                          ["Disiscritto", r.disiscritto_il, "text-zinc-500"]]
                          .filter(([, v]) => v).map(([l, v, cls]) => (
                            <span key={l} className={`text-[10px] font-bold ${cls}`}>{l} {fmtT(v)}</span>
                          ))}
                      </div>
                    </div>
                  ))}
                  {recipients.length === 0 && <div className="px-3 py-6 text-center text-xs text-zinc-500">Nessun invio registrato.</div>}
                </div>
              </>
            ) : <div className="py-10 text-center text-sm text-zinc-500">Caricamento...</div>}
          </div>
        </div>
      )}
    </div>
  );
}
